import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useAppState } from "@/app";
import {
  completeGithubRedirectLogin,
  consumeAuthReturnTo,
  createGithubSessionSeed,
  defaultSignedInPath,
  resetGithubRedirectLogin,
} from "@/auth";
import { AuthCard, type AuthCardStatus } from "@/components/base";
import { LoginButton } from "@/components/buttons";
import { isHttpClientError } from "@/http";
import { createLogger } from "@/logging";

const logger = createLogger("page/authCallback");

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { startSession } = useAppState();
  const hasHandledResult = useRef(false);
  const [status, setStatus] = useState<AuthCardStatus>({
    kind: "pending",
    message: "GitHub 로그인 응답을 확인하는 중입니다.",
  });

  useEffect(() => {
    let disposed = false;

    void (async () => {
      setStatus({ kind: "pending", message: "Hatchery Registry 세션을 생성하는 중입니다." });

      try {
        const loginResp = await completeGithubRedirectLogin();
        if (disposed || hasHandledResult.current) {
          return;
        }

        await startSession(createGithubSessionSeed(loginResp));
        hasHandledResult.current = true;
        // 로그인 전에 가려던 곳이 있으면 그곳으로 이동한다(딥링크·북마크 보존).
        // 없으면 기본 랜딩인 공용 데이터셋 목록으로 이동한다.
        navigate(consumeAuthReturnTo() ?? defaultSignedInPath, { replace: true });
      } catch (error) {
        if (disposed || hasHandledResult.current) {
          return;
        }

        hasHandledResult.current = true;
        logger.warn("Failed to process the OAuth callback.", getAuthCallbackErrorLogContext(error));
        setStatus({ kind: "failed", message: getAuthCallbackErrorMessage(error) });
      }
    })();

    return () => {
      disposed = true;
    };
  }, [navigate, startSession]);

  return (
    <AuthCard
      titleId="auth-callback-title"
      title={status.kind === "failed" ? "로그인하지 못했습니다." : "로그인을 처리하고 있습니다."}
      status={status}
    >
      {status.kind === "failed" ? (
        <LoginButton
          onClick={() => {
            resetGithubRedirectLogin();
            navigate("/", { replace: true });
          }}
        >
          로그인으로 돌아가기
        </LoginButton>
      ) : null}
    </AuthCard>
  );
}

function getAuthCallbackErrorMessage(error: unknown): string {
  if (isHttpClientError(error)) {
    switch (error.code) {
      case "auth.github.state.invalid":
      case "auth.github.token.invalid":
        return "GitHub 로그인 시간이 만료됐거나 요청을 확인하지 못했습니다. 다시 로그인해 주세요.";
      case "auth.github.org.forbidden":
        return "허용된 GitHub 조직 멤버만 Hatchery Registry에 접근할 수 있습니다.";
      case "auth.login.unsupported_type":
      case "auth.github.redirect_uri.not_allowed":
        return "GitHub 로그인 요청 설정이 올바르지 않습니다.";
      case "auth.github.error":
        return "GitHub과 통신하지 못했습니다. 잠시 후 다시 로그인해 주세요.";
      case "auth.rate_limit.exceeded":
        return "로그인 요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.";
      case "user.not_member":
        return "Hatchery Registry 멤버가 아닙니다. 관리자에게 문의해 주세요.";
    }

    if (error.status === 401) {
      return "GitHub 인증은 완료됐지만 Hatchery Registry 세션을 만들지 못했습니다.";
    }

    if (error.status === 403) {
      return "허용된 GitHub 조직 멤버만 Hatchery Registry에 접근할 수 있습니다.";
    }

    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "OAuth 로그인을 완료하지 못했습니다.";
}

function getAuthCallbackErrorLogContext(error: unknown): unknown {
  if (isHttpClientError(error)) {
    return error.toLogContext();
  }

  return error;
}
